import Session from './session';

// export type UserMoods = 'Happy' | 'Relaxed' | 'Tired' | 'Stressed' | 'Angry' | 'Sad';
export type UserMoods = 'Positive & In Control' | 'Uncertain & Cautious' | 'Struggling and Drained' | 'Neutral & Calm';
export type UserRoles = 'admin' | 'member' | 'model';
export type SubscriptionPlans = 'free' | 'premium';

export enum Roles {
    Admin = 'admin',
    Member = 'member',
    Model = 'model',
}

export interface SessionSummary {
    created_at: string; // when the summary was created
    text: string; // the summary of the session generated by the model
    category: string; // the category generated by the model
    session_id: string; // the session id
}

export interface MotivationalQuote {
    text: string;
    updated_at: string;
}

export interface Tip {
    title: string;
    description: string;
}

export interface RecommendedConversation {
    categoryTitle: string; // The title of the category. Example: 'Relationship challenges with family', 'Stress at work', 'Anxiety'
    topics: string[]; // The list of conversation topics recommended for that category. If title= 'Relationship challenges with family' examples of topics: ['How to communicate better with your family', 'How to set boundaries with your family', 'How to deal with family conflicts']
    tips: Tip[]; // The list of some actionable tips that the user can use to improve their mental health. If title= 'Relationship challenges with family' examples of tips: [{title:'You should communicate your feelings', description: 'You should communicate your feelings to your family members to avoid misunderstandings and conflicts'}, {title:'Set boundaries', description: 'Set boundaries with your family members to protect your mental health and avoid conflicts'}]
    lastTalk?: {
        sessionId: string; // The id of the last session that the user had in that category
        sessionTitle: string; // The title of the last session that the user had in that category
        sessionSummary: string; // The summary of the last session that the user had in that category
        openingMessage: string; // A message to encourage user to continue the conversation
    };
}

export interface Highlight {
    key: string; // A key describing the highlight
    value: string | number | boolean | Record<string, string | number | boolean>; // The value of the highlight
    createdAt: number; // The timestamp when the highlight was created
    sessionId?: string; // The session id where the highlight was created
}

export type SeverityLevel = 'Minimal' | 'Mild' | 'Moderate' | 'Moderately Severe' | 'Severe';

export interface Severity {
    severity: SeverityLevel;
    percentage: number;
    count: number;
}

export interface AssessmentResult {
    anxietySymptomsAverageScore: number;
    anxietySymptomsAverage: string;
    depressionSymptomsAverageScore: number;
    depressionSymptomsAverage: string;
    anxietySymptoms: Severity[];
    depressionSymptoms: Severity[];
}

export interface ActivationMetrics {
    totalUsers: number;
    activeUsers: number;
    activeUsersPercentage: number;
}

/**
 * Mirrors the api's `SubscriptionStatus` (`api/src/models/user.model.ts`).
 *
 * `'canceling'` was missing here and in the app for as long as both unions have
 * existed, while `payments.service.ts` actively writes it — a subscription the
 * user has cancelled but is still paid up for. Anything comparing against it was
 * therefore comparing against a value TypeScript believed impossible, and in the
 * app that silently made a whole branch dead. Keep this union in step with the
 * api's; it is a contract, not a local convenience.
 */
export type SubscriptionStatus = 'active' | 'canceling' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'unpaid';

export const CRM_TYPES = ['gift', 'paid'] as const;
export type CrmType = (typeof CRM_TYPES)[number];

export interface ShippingAddress {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
}

export default interface User {
    id: string;
    stripeCustomerId?: string;
    subscriptionId?: string | null;
    subscriptionStatus?: SubscriptionStatus;
    currentPeriodEnd?: number;
    cancellationReason?: 'subscription_canceled';
    name?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    activationMetrics?: ActivationMetrics;
    assessmentResult?: AssessmentResult;
    // analytics?: Analytics;
    created_at: string;
    updated_at: string;
    deleted?: boolean;
    deleted_at?: string;
    email: string;
    active: boolean;
    onBoarded: boolean;
    personaOnboarded: boolean;
    lastMood: {
        dailyMoodCheck: boolean;
    };
    mood: UserMoods | null;
    avatar?: string;
    subscriptionPlan: SubscriptionPlans;
    // Epoch-ms end of the Course-33 one-time access window (the gate marker).
    course33AccessExpiresAt?: number;
    sessions: Session[];
    role: UserRoles;
    bio?: string;
    token_credits: number;
    session_summaries?: {
        [sessionId: string]: SessionSummary;
    };
    motivationalQuote?: MotivationalQuote;
    sessionCategories?: string[];
    specialSessionIds: {
        educational?: string;
        recommendation?: string;
    };
    dailyTips: Tip[];
    dailyTipsLastGeneratedAt?: string;
    recommendedConversations: RecommendedConversation[];
    recommendedConversationsLastGeneratedAt?: string;
    billingCycle?: number;
    billingCycleTokensCount?: number;
    exceededBillingCycleLimit?: boolean;
    highlights: Highlight[];
    overallTokensCount?: number;
    overallChatTokensCount?: number;
    highlightsGeneratedAtTokenCount?: number;
    signupSource?: string;
    welcomeEmailSent?: boolean;
    referralCode?: string;
    referredByCode?: string;
    referredByUserId?: string;

    // ── Shipping (auto-populated from Stripe Checkout) ─────────────────────
    shippingAddress?: ShippingAddress;
    shippingName?: string;
    addressUpdatedAt?: string;
    addressSource?: 'stripe_checkout' | 'user_profile' | 'admin' | 'backfill';

    // ── Admin CRM fields (spec v2 §02) ─────────────────────────────────────
    // All optional and all unset on most existing users — that is the correct
    // value, not missing data. Eight are typed by an admin; the last four are
    // written by the API and are read-only here.
    crmType?: CrmType;
    cohort?: string;
    /**
     * A free-text note about who brought this person in. NOT the referral
     * programme (`referredByCode` / `referredByUserId`), which pays real
     * commission — never write one from the other.
     */
    referrerName?: string;
    linkedinUrl?: string;
    testimonialVideo?: boolean;
    testimonialPost?: boolean;
    adminNotes?: string;
    /** Private COMMUNITY GROUP membership — not the notification bot's mapping. */
    /** manual + auto — the customer's `@handle` (no leading @). Not a way to DM them. */
    telegramHandle?: string;
    telegramGroupJoined?: boolean;
    telegramGroupJoinedAt?: string;
    telegramGroupInviteSentAt?: string;
    /** Read-only mirror of the Stripe promotion code the buyer redeemed. */
    redeemedPromotionCode?: string;
    /** Denormalised mirror of the newest order's status, for the list column. */
    latestOrderStatus?: string;
}
